<template>
  <div class="create-group-modal">
    <b-button v-required-role="'ROLE_BACKOFFICE_GROUP_POST'" @click="openModal" size="sm" variant="success">
      {{ $t('configuration.button.add') }}
    </b-button>
    <b-modal
        ref="create-configuration-modal"
        size="xl"
        :hide-footer="isLoading"
    >
      <div v-if="isLoading" class="d-flex justify-content-center m-5">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">{{ $t('state.loading') }}</span>
        </div>
      </div>
      <template v-if="!isLoading" #modal-header>
        <div class="w-100 d-flex justify-content-between align-items-center">
          <h4>{{ $t('configuration.add_new_configuration.title') }}</h4>
          <div class="d-flex">
            <b-button type="button" class="mr-1" variant="secondary" @click="hideModal">
              {{ $t('configuration.button.cancel') }}
            </b-button>
            <b-button variant="primary" @click="saveConfiguration">
              {{ !isConfiguration ? $t('configuration.button.add') : $t('configuration.button.edit') }}
            </b-button>
          </div>
        </div>
      </template>
      <b-card v-if="!isLoading">
        <b-card-body>
          <b-form-group
              v-required-role="'ROLE_BACKOFFICE_GROUP_POST'"
              :label="$t('configuration.key')"
              label-for="key"
          >
            <b-form-input
                v-model="key"
                @input="$v.key.$model = $event"
                :state="!$v.key.$dirty ? null :(!$v.key.$error)"
                :disabled="isConfiguration"
                trim></b-form-input>
            <p v-if="$v.key.$dirty && !$v.key.required" class="text-danger">{{
                $t('configuration.errors.required')
              }}</p>
          </b-form-group>

          <div id="json-editor" ref="json-editor" class="jsoneditor-container"></div>

          <b-form-group
              v-required-role="'ROLE_BACKOFFICE_GROUP_POST'"
              :label="$t('configuration.group')"
              label-for="Group"
              :disabled="isConfiguration"
          >
            <b-form-input
                v-model="group" trim>
            </b-form-input>
          </b-form-group>

          <b-form-group
              v-required-role="'ROLE_BACKOFFICE_GROUP_POST'"
              :label="$t('configuration.tags')"
              label-for="Tags"
          >
            <b-form-input
                v-model="tags" trim>
            </b-form-input>
          </b-form-group>

          <b-form-group v-required-role="'ROLE_BACKOFFICE_GROUP_POST'">
            <b-form-checkbox
                id="checkbox-"
                v-model="exposeInApi"
                :name="$t('configuration.is_expose_in_api')"
            >
              {{ $t('configuration.is_expose_in_api') }}
            </b-form-checkbox>
          </b-form-group>

          <b-form-group v-required-role="'ROLE_BACKOFFICE_GROUP_POST'">
            <b-form-checkbox
                id="checkbox-1"
                v-model="isAuditable"
                :name="$t('configuration.is_auditable')"
            >
              {{ $t('configuration.is_auditable') }}
            </b-form-checkbox>
          </b-form-group>

          <b-form-group
              v-required-role="'ROLE_BACKOFFICE_GROUP_POST'"
              :label="$t('configuration.generator_class')"
              label-for="generator-class"
          >
            <b-form-select v-model="generatorClass" :options="generatorClassOptions"></b-form-select>
          </b-form-group>

          <b-form-group
              v-if="generatorClass === 'JSON'"
              v-required-role="'ROLE_BACKOFFICE_GROUP_POST'"
              :label="$t('configuration.generator_config')"
              label-for="generator-config"
          >
            <b-form-input
                v-model="generatorConfig" trim>
            </b-form-input>
          </b-form-group>
        </b-card-body>
      </b-card>
      <template v-if="!isLoading" #modal-footer>
        <div class="w-100 d-flex justify-content-end">
          <b-button type="button" class="mr-1" variant="secondary" @click="hideModal">
            {{ $t('configuration.button.cancel') }}
          </b-button>
          <b-button variant="primary" @click="saveConfiguration">
            {{ !isConfiguration ? $t('configuration.button.add') : $t('configuration.button.edit') }}
          </b-button>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import axios from "axios";
import {required} from 'vuelidate/lib/validators';
import JSONEditor from 'jsoneditor';
import alertify from 'alertifyjs';

export default {
  data() {
    return {
      key: '',
      group: null,
      generatorClass: '',
      generatorConfig: '',
      isAuditable: false,
      exposeInApi: false,
      tags: '',
      editor: null,
      item: null,
      json: {},
      jsonError: [],
      isLoading: true,
      isConfiguration: false,
      generatorClassOptions: ['none', 'JSON'],
      errors: {
        required: 'This field is required!'
      }
    }
  },
  validations: {
    key: {
      required
    }
  },
  mounted() {
    this.openModal();
  },
  methods: {
    loadJSONEditor() {
      return new Promise(resolve => {
        let script = document.createElement('script')

        script.setAttribute('src', 'https://cdnjs.cloudflare.com/ajax/libs/jsoneditor/9.4.2/jsoneditor.min.js')
        document.head.appendChild(script);

        script.onload = () => {
          resolve();
        }
      })
          .then(() => {
            const container = document.getElementById("json-editor");
            const options = {
              mode: 'code',
              enableTransform: false
            };
            this.editor = new JSONEditor(container, options);
            const initialJson = this.json;

            this.editor.set(initialJson);
          });
    },
    saveConfiguration() {
      this.editor.validate().then(data => {
        if (!data.length) {
           this.validAndSend();
        }
      });
    },
    validAndSend() {
      if (!this.$v.$invalid) {
        const httpMethod = this.isConfiguration ? 'put' : 'post';
        const url = httpMethod === 'post' ? '/api/config/configuration' : `/api/config/configuration/${this.key}/${this.group}`;

        const dataToSend = {
          isAuditable: this.isAuditable,
          exposeInApi: this.exposeInApi
        }

        if (httpMethod === 'post') {
          dataToSend.configKey = this.key;

          if (this.group) {
            dataToSend.configGroup = this.group;
          }
        }

        if (this.json) {
          dataToSend.value = this.editor.get();
        }

        if (this.generatorClass) {
          dataToSend.generatorClass = this.generatorClass;
        }

        if (this.generatorConfig) {
          dataToSend.generatorConfig = this.generatorConfig;
        }

        if (this.tags) {
          dataToSend.tags = this.tags;
        }

        axios[httpMethod](url, dataToSend)
            .then(() => {
              this.hideModal();
              alertify.success('notify.configuration_save.success');
              this.$parent.loadItems();
            })
        .catch(error => {
          alertify.error(error.response.data.label);
        });
      }
    },
    setEditData(item) {
      if (item) {
        axios.get(`/api/config/configuration/${item.config_key}/${item.config_group}`)
            .then(response => {
              const data = response.data;
              this.key = data.config_key;
              this.group = data.config_group;
              this.generatorClass = data.generator_class;
              this.generatorConfig = data.generator_config;
              this.isAuditable = data.is_auditable;
              this.exposeInApi = data.exposeInApi;
              this.tags = data.tags;
              this.json = data.value;
              this.isLoading = false;
              this.isConfiguration = true;

              this.loadJSONEditor();
            })
            .catch(error => {
              console.log(error);
            });
      }
    },
    openModal() {
      if (this.item && this.item.config_key) {
        this.setEditData(this.item);
      } else {
        this.isLoading = false;
        this.loadJSONEditor();
      }
      this.$refs['create-configuration-modal'].show();
    },
    hideModal() {
      this.$refs['create-configuration-modal'].hide();
    },
    resetProperties() {
      this.key = '';
      this.group = '';
      this.json = {};
      this.generatorClass = '';
      this.generatorConfig = '';
      this.tags = '';
      this.isAuditable = false;
      this.exposeInApi = false;
      this.isConfiguration = false;
      this.isLoading = true;
      this.$v.$reset();
    }
  }
}
</script>

<style>

</style>
